import {
  AddCircle,
  Approval,
  PersonPin,
  AllInbox,
  DoneAll,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GenButton,
  GenSearch,
  GenTabs,
  RefreshButton,
} from '@pinnacleautomotive/clarity-components';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CompanyStatus, ProcessStep } from '../../common/enums/enums';
import { useRefresh } from '../../hooks/useRefresh';
import useVendorContracts from '../../hooks/useVendorContracts';
import { setDoneOrdersFilters } from '../../redux/actions/orders/doneOrdersActions';
import { clearNewOrder } from '../../redux/actions/orders/newOrderActions';
import {
  setAllOrdersFiltersByCompany,
  setAllOrdersSortDetails,
} from '../../redux/actions/workQueue/allOrdersActions';
import { invalidateQuery } from '../../redux/rtk/orders.services';
import { useGetVendorsQuery } from '../../redux/rtk/vendors.services';
import { selectDoneFilters } from '../../redux/selectors/orders/doneOrdersSelector';
import {
  selectMyOrdersTotalWO,
  selectIsLoading,
} from '../../redux/selectors/orders/myOrdersSelectors';
import {
  selectUnapprovedTotalWO,
  selectIsLoading as unIsLoading,
} from '../../redux/selectors/orders/unapprovedSelectors';
import {
  currentUserCompanyId,
  isApprover,
  selectPersonId,
  selectUserCurrentCompanyStatus,
  selectUserDepartmentsApproverRights,
} from '../../redux/selectors/userDataSelectors';
import {
  allOrdersSortEnabled,
  allOrdersSortBy,
  allOrdersSortField,
  selectAllOrdersFiltersByCompany,
} from '../../redux/selectors/workQueue/allOrdersSelectors';
import AllOrdersContainer from './allOrders';
import Filters from './allOrders/Filters';
import FilterWoCount from './allOrders/FilterWoCount';
import { BODY_BUILDER_BY_TAB } from './allOrders/utils';
import NewOrderAndServiceOrderButtons from './components/NewOrderAndServiceOrderButtons';
import ToggleViewButtons from './components/ToggleViewButtons';
import ViewTabsOrdersOrVehicle from './components/ViewTabsOrdersOrVehicle';
import MyOrdersContainer from './myOrders';
import NewOrderCreationAttentionModal from './newOrder/components/NewOrderCreationAttentionModal';
import UnapprovedContainer from './unapproved';
import { ViewOrderOrVehicleProvider } from './ViewOrdersOrVehicle.provider';

const PREFIX = 'OrdersMainPage';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  tabsContainer: `${PREFIX}-tabs`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.mainContainer}`]: {
    flexDirection: 'column',
  },
  [`& .${classes.tabsContainer}`]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const Container = ({
  children,
  customFilters,
  searchQuery,
  setSearchQuery,
  totalWorkOrders,
  isLoading,
  tab,
}) => {
  const { t } = useTranslation(['orders', 'global']);
  const theme = useTheme();

  return (
    <Stack gap={1}>
      <Stack
        direction={{ xs: 'column-reverse', md: 'row-reverse' }}
        gap={2}
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems={{ xs: 'start', md: 'center' }}
        pt={1}
      >
        {customFilters}
        {!customFilters && (
          <Grid container justifyContent={'space-between'}>
            <Grid item xs={12} md={6}>
              <GenSearch
                placeholder={
                  <Trans
                    t={t}
                    i18nKey="orders:search"
                    components={{ 1: <br /> }}
                  />
                }
                searchTerm={setSearchQuery}
                sx={{
                  order: 1,
                  width: '100%',
                  [theme.breakpoints.up('md')]: {
                    width: '336px',
                  },
                  '& form': { margin: '0px !important', height: '100%' },
                  marginBottom: { xs: 0, md: 1 },
                }}
              />
            </Grid>
            <Stack
              direction="row"
              gap={3}
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: { xs: 'inherit', md: 'auto' } }}
              pt={{ xs: 2, md: 0 }}
              pb={{ xs: 1, md: 0 }}
            >
              <FilterWoCount
                totalWorkOrders={totalWorkOrders}
                isLoading={isLoading}
                tab={tab}
                searchQuery={searchQuery}
              />
              {tab && <ToggleViewButtons tab={tab} />}
            </Stack>
          </Grid>
        )}
      </Stack>
      {children}
    </Stack>
  );
};

const AllOrdersComponent = ({
  personId,
  companyId,
  showUpdateAlert,
  allOrdersFilters,
}) => {
  const { t } = useTranslation('orders');
  const dispatch = useDispatch();

  const setAllOrdersFiltersHandler = newFilters => {
    dispatch(
      setAllOrdersFiltersByCompany({
        companyId,
        filters: newFilters,
      })
    );
  };

  const handleClear = () => {
    setAllOrdersFiltersHandler({
      searchQuery: '',
      selectedSteps: [],
      selectedVendors: [],
      selectedDepartments: [],
      dateRange: {
        startingDate: null,
        endDate: null,
        formattedDate: {},
      },
    });
  };

  return (
    <Container
      customFilters={
        <Filters
          handleClear={handleClear}
          currentFilters={allOrdersFilters}
          onFilter={setAllOrdersFiltersHandler}
          startPlaceholder={t(
            'AllOrdersTab.dateRange.allOrders.startPlaceholder'
          )}
          endPlaceholder={t('AllOrdersTab.dateRange.allOrders.endPlaceholder')}
        >
          <ToggleViewButtons tab="AllOrders" />
        </Filters>
      }
    >
      <ViewTabsOrdersOrVehicle tab="AllOrders">
        <AllOrdersContainer
          personId={personId}
          companyId={companyId}
          filters={allOrdersFilters}
          showAlert={showUpdateAlert}
        />
      </ViewTabsOrdersOrVehicle>
    </Container>
  );
};

const Orders = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['orders', 'global']);
  const history = useHistory();
  const currentUserIsApprover = useSelector(isApprover);
  const personId = useSelector(selectPersonId);
  const companyId = useSelector(currentUserCompanyId);
  const userCompanyStatus = useSelector(selectUserCurrentCompanyStatus);
  const departments = useSelector(selectUserDepartmentsApproverRights);
  const myOrdersTotalWo = useSelector(selectMyOrdersTotalWO);
  const myOrdersIsLoading = useSelector(selectIsLoading);
  const unapprovedTotalWo = useSelector(selectUnapprovedTotalWO);
  const unapprovedIsLoading = useSelector(unIsLoading);
  const allOrdersFilters = useSelector(
    selectAllOrdersFiltersByCompany(companyId)
  );

  const sortEnabled = useSelector(allOrdersSortEnabled);
  const sortColumnField = useSelector(allOrdersSortField);
  const sortColumnBy = useSelector(allOrdersSortBy);
  const doneOrdersFilters = useSelector(selectDoneFilters(companyId));
  const { hasReconditioning, hasServiceOrder, isLoading, isFetching } =
    useVendorContracts();

  const [searchQuery, setSearchQuery] = useState('');
  const [showNewOrderCreationAttention, setShowNewOrderCreationAttention] =
    useState(false);

  const { refresh, lastRefresh } = useRefresh();
  const { data: vendors = [] } = useGetVendorsQuery(companyId);
  const isVendorCompanyActive = vendors.some(
    vendor => vendor?.companyStatus === CompanyStatus.ACTIVE
  );
  const deactivatedVendorNames = vendors
    .filter(vendor => vendor?.companyStatus !== CompanyStatus.ACTIVE)
    .map(vendor => vendor.name);

  const NewWorkOrderAndReconButtons = hasServiceOrder ? (
    <NewOrderAndServiceOrderButtons buttonStatus={userCompanyStatus} />
  ) : (
    <GenButton
      variant="contained"
      disabled={!userCompanyStatus}
      startIcon={<AddCircle />}
      text={t('createNew')}
      onClick={() => goToNewOrder()}
      displayFab={true}
    />
  );

  const setDoneOrdersFiltersHandler = newFilters => {
    dispatch(
      setDoneOrdersFilters({
        companyId,
        filters: newFilters,
      })
    );
  };

  const handleClearDoneOrder = () => {
    setDoneOrdersFiltersHandler({
      searchQuery: '',
      selectedVendors: [],
      selectedDepartments: [],
      selectedSteps: [ProcessStep.DONE],
      dateRange: {
        startingDate: null,
        endDate: null,
        formattedDate: {},
      },
    });
  };

  const tabs = {
    allOrders: {
      name: t('allOrders'),
      component: (
        <AllOrdersComponent
          key={lastRefresh}
          personId={personId}
          companyId={companyId}
          showUpdateAlert={true}
          allOrdersFilters={allOrdersFilters}
          sortEnabled={sortEnabled}
        />
      ),
      icon: <AllInbox />,
      button: (
        <RefreshButton
          onClick={() => {
            dispatch(
              invalidateQuery(
                BODY_BUILDER_BY_TAB.AllOrders(
                  companyId,
                  1,
                  allOrdersFilters,
                  sortEnabled,
                  sortColumnBy,
                  sortColumnField
                )
              )
            );
            dispatch(setAllOrdersSortDetails({ sortEnabled: false }));
            refresh();
          }}
        />
      ),
    },
    unaproved: {
      name: t('unapproved'),
      component: (
        <Container
          setSearchQuery={setSearchQuery}
          totalWorkOrders={unapprovedTotalWo}
          isLoading={unapprovedIsLoading}
          key={lastRefresh}
        >
          <UnapprovedContainer
            companyId={companyId}
            departments={departments}
            searchQuery={searchQuery}
          />
        </Container>
      ),
      icon: <Approval />,
      button: <RefreshButton onClick={() => refresh()} />,
    },
    myOrders: {
      name: t('myOrders'),
      component: (
        <Container
          setSearchQuery={setSearchQuery}
          totalWorkOrders={myOrdersTotalWo}
          isLoading={myOrdersIsLoading}
          key={lastRefresh}
          tab="MyOrders"
        >
          <ViewTabsOrdersOrVehicle
            tab="MyOrders"
            optionalSearchQuery={searchQuery}
          >
            <MyOrdersContainer
              personId={personId}
              companyId={companyId}
              searchQuery={searchQuery}
            />
          </ViewTabsOrdersOrVehicle>
        </Container>
      ),
      icon: <PersonPin />,
      button: (
        <RefreshButton
          onClick={() => {
            dispatch(
              invalidateQuery(
                BODY_BUILDER_BY_TAB.MyOrders(
                  companyId,
                  1,
                  {
                    personId,
                  },
                  searchQuery
                )
              )
            );
            refresh();
          }}
        />
      ),
    },
    doneOrders: {
      name: t('doneOrders'),
      component: (
        <Container
          setSearchQuery={setSearchQuery}
          key={lastRefresh}
          tab="DoneOrders"
          customFilters={
            <Filters
              currentFilters={doneOrdersFilters}
              onFilter={setDoneOrdersFiltersHandler}
              handleClear={handleClearDoneOrder}
              hideFilterBySteps={true}
              dateRangeFlexGrow={0.5}
              startPlaceholder={t(
                'AllOrdersTab.dateRange.done.startPlaceholder'
              )}
              endPlaceholder={t('AllOrdersTab.dateRange.done.endPlaceholder')}
              isDoneTab={true}
            >
              <ToggleViewButtons tab="DoneOrders" />
            </Filters>
          }
        >
          <ViewTabsOrdersOrVehicle
            tab="DoneOrders"
            optionalSearchQuery={searchQuery}
          >
            <AllOrdersContainer
              personId={personId}
              companyId={companyId}
              filters={doneOrdersFilters}
              isDoneTab={true}
              searchQuery={searchQuery}
            />
          </ViewTabsOrdersOrVehicle>
        </Container>
      ),
      icon: <DoneAll />,
      button: (
        <RefreshButton
          onClick={() => {
            dispatch(
              invalidateQuery(
                BODY_BUILDER_BY_TAB.DoneOrders(companyId, 1, {
                  personId,
                  ...doneOrdersFilters,
                })
              )
            );
            refresh();
          }}
        />
      ),
    },
  };
  if (!currentUserIsApprover || hasReconditioning) delete tabs.unaproved;

  const goToNewOrder = () => {
    if (isVendorCompanyActive) {
      history.push('/orders/new');
      dispatch(clearNewOrder());
    }

    setShowNewOrderCreationAttention(!isVendorCompanyActive);
  };

  return (
    <>
      <StyledGrid className={classes.mainContainer}>
        <Grid item>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            py={2}
            px={3}
          >
            <Stack flexGrow={1}>
              <Typography variant="h6">{t('orders:title')}</Typography>
              <Typography variant="body1">{t('orders:subtitle')}</Typography>
            </Stack>
            {isLoading || isFetching ? (
              <LoadingButton
                variant="contained"
                loading={true}
                disabled={!userCompanyStatus}
                size="small"
                data-testid="orders-loading-button"
              >
                {t('createWorkOrder')}
              </LoadingButton>
            ) : (
              NewWorkOrderAndReconButtons
            )}
          </Stack>
          <Grid container className={classes.tabsContainer}>
            <GenTabs tabs={tabs} />
          </Grid>
        </Grid>
      </StyledGrid>
      <NewOrderCreationAttentionModal
        open={showNewOrderCreationAttention}
        storeNames={deactivatedVendorNames}
        onClose={() => setShowNewOrderCreationAttention(false)}
      />
    </>
  );
};

export default function OrdersContainer() {
  return (
    <ViewOrderOrVehicleProvider>
      <Orders />
    </ViewOrderOrVehicleProvider>
  );
}
